import cn from 'classnames'
import type {EventDocument} from 'prismicio-types'
import type {FC} from 'react'
import {memo, useCallback, useEffect, useMemo, useState} from 'react'
import {CustomSelect} from 'slices/IntegrationsContainer/CustomSelect'
import type {EventType} from 'src/common/utils/eventRoutes'
import {formatEventTypeDisplay} from 'src/common/utils/eventRoutes'
import type {useEventLabels} from 'src/Event/hooks/useEventLabels'

type EventFiltersProps = {
  events: EventDocument<string>[]
  eventTypes: EventType[]
  activeType: EventType | null
  onTypeChange: (type: EventType | null) => void
  onFilterChange: (filteredEvents: EventDocument<string>[]) => void
  labels: ReturnType<typeof useEventLabels>['filters']
}

type LanguageOption = string | 'all'

const EventFiltersComponent: FC<EventFiltersProps> = ({
  events,
  eventTypes,
  activeType,
  onTypeChange,
  onFilterChange,
  labels,
}) => {
  const [language, setLanguage] = useState<LanguageOption>('all')

  const availableLanguages = useMemo(() => {
    const languages = new Set<string>()

    events.forEach((event) => {
      if (event.data.language) {
        languages.add(event.data.language)
      }
    })

    return Array.from(languages).sort()
  }, [events])

  const filteredEvents = useMemo(() => {
    let result = [...events]

    if (language !== 'all') {
      result = result.filter((event) => event.data.language === language)
    }

    return result
  }, [events, language])

  // Only include filteredEvents in deps since onFilterChange should be stable
  useEffect(() => {
    onFilterChange(filteredEvents)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onFilterChange is a stable callback from parent
  }, [filteredEvents])

  // Handle event type pill click
  const handleTypeClick = useCallback(
    (type: string) => {
      if (type === labels.allFilter) {
        onTypeChange(null) // Show all types
      } else {
        onTypeChange(type as EventType)
      }
    },
    [labels.allFilter, onTypeChange],
  )

  const allTypes = useMemo(() => [labels.allFilter, ...eventTypes] as const, [eventTypes, labels.allFilter])

  const eventTypeOptions = useMemo(
    () =>
      allTypes.map((type) => ({
        category_name: formatEventTypeDisplay(type),
        value: type,
      })),
    [allTypes],
  )

  const activeEventTypeIndex = useMemo(() => {
    if (!activeType) {
      return 0 // First option is "All"
    }

    return allTypes.findIndex((type) => type === activeType)
  }, [activeType, allTypes])

  const handleEventTypeChange = useCallback(
    (index: number) => {
      const selectedType = allTypes[index]
      handleTypeClick(selectedType)
    },
    [allTypes, handleTypeClick],
  )

  const languageOptions = useMemo(
    () => [
      {category_name: 'All Languages', value: 'all'},
      ...availableLanguages.map((lang) => ({category_name: lang, value: lang})),
    ],
    [availableLanguages],
  )

  const activeLanguageIndex = useMemo(() => {
    const index = languageOptions.findIndex((option) => option.value === language)

    return index === -1 ? 0 : index
  }, [language, languageOptions])

  const handleLanguageChange = useCallback(
    (index: number) => {
      const selectedValue = languageOptions[index].value
      setLanguage(selectedValue)
    },
    [languageOptions],
  )

  return (
    <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:gap-2 lg:justify-between">
      <div className="flex w-full items-center gap-2 md:flex-1 lg:flex-initial">
        <div className="hidden flex-wrap items-center gap-2 lg:flex">
          {allTypes.map((type) => {
            const isActive = type === labels.allFilter ? !activeType : type === activeType

            return (
              <button
                key={type}
                onClick={() => handleTypeClick(type)}
                className={cn(
                  'text-button-s flex h-[38px] items-center justify-center rounded-[4px] px-3 transition-colors',
                  {
                    'bg-green-neon text-black': isActive,
                    'border border-solid border-black/10 bg-moss-white text-moss-grey-500 hover:bg-moss-grey-100':
                      !isActive,
                  },
                )}
              >
                {formatEventTypeDisplay(type)}
              </button>
            )
          })}
        </div>

        <div className="w-full lg:hidden">
          <CustomSelect
            options={eventTypeOptions}
            activeIndex={activeEventTypeIndex}
            setActiveIndex={handleEventTypeChange}
            v2
            trackingData={{
              pageName: 'events',
              dataType: 'event-type-filter',
            }}
          />
        </div>
      </div>

      <div className="flex-1 md:flex-1 lg:w-[180px] lg:flex-none">
        <CustomSelect
          options={languageOptions}
          activeIndex={activeLanguageIndex}
          setActiveIndex={handleLanguageChange}
          v2
          trackingData={{
            pageName: 'events',
            dataType: 'language-filter',
          }}
        />
      </div>
    </div>
  )
}

export const EventFilters = memo(EventFiltersComponent)
